'use client'

import dynamic from 'next/dynamic'
import {
  type FormEvent,
  useCallback,
  useReducer,
  useRef,
  useState,
} from 'react'
import { trackFreeToolEvent } from '@/lib/site/gtm'
import { useSetAuditedEmail } from './audited-email-context'
import { EmailAuditInputCard } from './email-audit-input-card'
import { EmailAuditReportSkeleton } from './email-audit-report-skeleton'
import { requestFreeEmailAudit } from './email-audit-request'
import {
  emailAuditInputProblem,
  emailAuditToolReducer,
  INITIAL_EMAIL_AUDIT_TOOL_STATE,
  nextEmailAuditLoad,
} from './email-audit-tool-state'
import { useEmailAuditCopy } from './use-email-audit-copy'

/** The sample: one realistic email with one issue for each kind of check. */
const SAMPLE_EMAIL = {
  path: '/landing/email-audit/sample-email.html',
  subject: 'LAST CHANCE!!! 40% off ends tonight',
  previewText: '',
} as const

// The report (findings, preview frame, filters) is its own chunk: a visitor
// who never runs an audit never downloads it.
const EmailAuditReport = dynamic(
  () =>
    import('./email-audit-report').then((module) => module.EmailAuditReport),
  { loading: EmailAuditReportSkeleton }
)

/**
 * The free audit island: the input card until a report arrives, then the
 * report in its place. Event-driven throughout (the reducer in
 * `email-audit-tool-state.ts`), so it carries no effects.
 */
export function EmailAuditTool() {
  const [state, dispatch] = useReducer(
    emailAuditToolReducer,
    INITIAL_EMAIL_AUDIT_TOOL_STATE
  )
  const [isSampleLoading, setIsSampleLoading] = useState(false)
  const setAuditedEmail = useSetAuditedEmail()
  const anchorRef = useRef<HTMLDivElement>(null)
  const copy = useEmailAuditCopy()

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (state.status.kind === 'running' || state.pendingLoad !== null) {
        return
      }
      const problem = emailAuditInputProblem(state.html)
      if (problem) {
        dispatch({ type: 'inputError', message: copy.form[problem] })
        return
      }
      const html = state.html
      dispatch({ type: 'started' })
      trackFreeToolEvent({ tool: 'email-audit', action: 'run' })
      const result = await requestFreeEmailAudit({
        html,
        ...(state.subject.trim() ? { subject: state.subject } : {}),
        ...(state.previewText.trim() ? { previewText: state.previewText } : {}),
      })
      if (!result.ok) {
        trackFreeToolEvent({ tool: 'email-audit', action: 'failed' })
        dispatch({ type: 'failed', message: copy.error(result.error) })
        return
      }
      setAuditedEmail({ html })
      trackFreeToolEvent({
        tool: 'email-audit',
        action: 'completed',
        mode: result.report.mode,
        verdict: result.report.audit.completion.readiness,
      })
      dispatch({ type: 'succeeded', report: result.report, html })
      anchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    [
      copy,
      setAuditedEmail,
      state.html,
      state.pendingLoad,
      state.previewText,
      state.status.kind,
      state.subject,
    ]
  )

  const handleSample = useCallback(async () => {
    // The reducer drops this download if the visitor edits, picks a file or
    // starts an audit before it lands.
    const load = nextEmailAuditLoad()
    dispatch({ type: 'loadStarted', load })
    setIsSampleLoading(true)
    try {
      const response = await fetch(SAMPLE_EMAIL.path)
      if (!response.ok) {
        throw new Error(`sample ${response.status}`)
      }
      dispatch({
        type: 'sample',
        load,
        html: await response.text(),
        subject: SAMPLE_EMAIL.subject,
        previewText: SAMPLE_EMAIL.previewText,
      })
    } catch {
      dispatch({
        type: 'loadFailed',
        load,
        message: copy.form.sampleFailed,
      })
    } finally {
      setIsSampleLoading(false)
    }
  }, [copy])

  const handleAuditAnother = useCallback(() => {
    // The report is gone, so the band below no longer offers "this email".
    setAuditedEmail(null)
    dispatch({ type: 'reset' })
    anchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [setAuditedEmail])

  return (
    <div
      ref={anchorRef}
      className="flex w-full scroll-mt-28 flex-col items-center"
    >
      {state.status.kind === 'done' ? (
        <EmailAuditReport
          report={state.status.report}
          html={state.status.html}
          onAuditAnother={handleAuditAnother}
        />
      ) : (
        <EmailAuditInputCard
          state={state}
          dispatch={dispatch}
          onSubmit={handleSubmit}
          onSample={handleSample}
          isSampleLoading={isSampleLoading}
        />
      )}
    </div>
  )
}
